import React, { useState, useEffect } from "react";
import { match } from "react-router";
import { Input, Heading, Link, Button, Text, Checkbox, PasswordStrength, InputForm } from "src/components";
import { getRoutePath, MatchParams, Page } from "src/routes";
import { AuthStore, RoutingStore } from "src/stores";
import { useTranslation } from "react-i18next";
import { Colors } from "src/types";
import { inject, observer } from "mobx-react";
import Box from "react-raster";
import { Paper } from "src/components/paper";
import { theme } from "src/config";

export interface ICreateAccountProps {
  authStore?: AuthStore;
  routingStore?: RoutingStore;
  match: match<MatchParams>; // This is injected so it has to be called match
}

export const CreateAccount = inject(
  "routingStore",
  "authStore"
)(
  observer((props: ICreateAccountProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = React.useState<string>("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [terms, setTermsChecked] = useState(false);
    const [error, setError] = useState(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [valid, setValid] = React.useState<boolean>(true);
    const [showPassword, setShowPassword] = React.useState(false);

    const togglePasswordVisiblity = () => {
      setShowPassword(!showPassword);
    };


    const submit = async () => {
      setError(false);
      const result = await props.authStore.signUp(email, password);
      if (result) {
        props.routingStore.push(`/${Page.Home}`);
      } else {
        setError(true);
      }
    };

    return (
      <Paper $noShadow style={{ ...theme.paper.small, marginTop: '10vh' }} $center>
        <Box gridRowGap={theme.layout.gutter} marginBottom='2rem'>
          <Heading type="h2">{t("create_account")}</Heading>
          <Heading type="h4">{t("welcome")}</Heading>
          <Box>
            <hr />
          </Box>
          {error && (
            <Text type="a" show={error}>
              {t("error_create_account")}
            </Text>
          )}
          <Box>
            <InputForm gap={"2rem"}>
              <Input
                key="email"
                type="email"
                label={t("email")}
                id="email"
                value={email}
                onChange={(text) => setEmail(text)}
                errorMessage={t("validation_error", { what: t("email") })}
              />
              <Text>{t("new_password_requirements")}</Text>

              <Input
                key="password"
                type={showPassword ? "text" : "password"}
                label={t("password")}
                id="password"
                icons={[
                  {
                    iconId: showPassword ? "eye" : "eyeClosed",
                    action: togglePasswordVisiblity,
                  }
                ]}
                value={password}
                onChange={(text) => setPassword(text)}
                errorMessage={t("validation_error", { what: t("password") })}
                onClickIcon={togglePasswordVisiblity}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                }}
              />

              <Input
                key="password_confirm"
                type={showPassword ? "text" : "password"}
                label={t("confirm_password")}
                id="password_confirm"
                icons={[
                  {
                    iconId: showPassword ? "eye" : "eyeClosed",
                    action: togglePasswordVisiblity,
                  }
                ]}
                value={confirmPassword}
                onChange={(text) => setConfirmPassword(text)}
                errorMessage={t("validation_error", { what: t("password") })}
                onClickIcon={togglePasswordVisiblity}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                }}
              />
            </InputForm>

          </Box>

          <Box>
            <PasswordStrength
              password={password}
              passwordRepeat={confirmPassword}
              onValidate={(v) => setValid(v)}
            />
          </Box>

          <Box style={{ marginTop: '2rem' }}>
            <Checkbox
              checked={terms}
              disabled={false}
              size="small"
              label={t("i_accept_terms")}
              onClick={() => setTermsChecked(!terms)}
            />
          </Box>

          <Box justifyContent="right">
            <Button
              size="default"
              variant="primary"
              disabled={!valid || !email.length || !terms}
              onClick={submit}
            >
              {t("create_account")}
            </Button>
          </Box>

          <Text size="xsmall">
            {t("policies")}{" "}
            <Link size="xsmall" color={Colors.Secondary}>
              {t("terms_of_use")}
            </Link>{" "}
            {t("and")}{" "}
            <Link size="xsmall" color={Colors.Secondary}>
              {t("privacy_policies")}
            </Link>
            .
          </Text>
        </Box>
        <Text size="small" as="p">
              {t("go_back_to_login")}{"? "}
            <Link
              size    = "small"
              text    = {t("click_here")}
              color   = {theme.color.primary7}
              onClick = {() =>
                props.routingStore.push(getRoutePath(Page.Login))
              }
            />
          </Text>
      </Paper>
    )
  }));

